import { Button } from '@creditornot/cb-button';
import { Cross, MagnifyingGlass, Plus } from '@creditornot/cb-icons';
import { ModalFooter } from '@creditornot/cbt-modal';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import styled from 'styled-components';

import { localize } from 'common/utils';
import { FilterMenu } from 'modules/shared-menu/components/FilterMenu';
import { buildVenueFilterMenuItems } from 'modules/shared-menu/components/FilterMenu/utils';
import { SelectableAccordion, useSelectableAccordion } from 'modules/shared-menu/components/SelectableAccordion';
import { useVenueList } from 'modules/shared-menu/hooks/useVenueList';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { ShareWithVenuesModal } from 'modules/shared-menu/pages/SharedMenuPage/components/ShareWithVenuesModal/ShareWithVenuesModal';
import { StopSharingWithVenuesModal } from 'modules/shared-menu/pages/SharedMenuPage/components/StopSharingWithVenuesModal';
import { SharedMenu } from 'modules/shared-menu/types';
import { getSelectionState } from 'modules/shared-menu/utils';

import { SelectedVenuesV2 } from '../SelectedVenuesV2';
import { VenueListFooter } from '../VenueListFooter';
import VenueListRowRenderer, { VenueRowData } from '../VenueListRowRenderer';
import {
  SidepanelContent,
  SidepanelContentComponent,
  SidepanelHeader,
  SidepanelSearchInput,
  SidepanelToolbar,
  SidepanelRoot,
  SidepanelToolbarLeft,
  SidepanelToolbarRight,
  SidepanelVenueList,
  SidepanelEmptyMessage,
} from '../sidepanelStyles';

type VenueManagementSidepanelProps = {
  allVenues: VenueWithSharedMenuLinkInfo[];
  onClose: () => void;
  selection: SharedMenu | undefined;
};

const ROW_HEIGHT = 56;

const StopSharingButton = styled(Button).attrs({ size: 'small', variant: 'lightRed', icon: <Cross /> })``;

const AddVenuesButton = styled(Button).attrs({ size: 'small', variant: 'lightBlue', icon: <Plus /> })``;

export const VenueManagementSidepanel = ({ allVenues, onClose, selection }: VenueManagementSidepanelProps) => {
  const { formatMessage } = useIntl();

  const [isShareMenuModalOpen, setIsShareMenuModalOpen] = useState(false);
  const [isStopSharingModalOpen, setIsStopSharingModalOpen] = useState(false);

  const venuesLinkedToSharedMenu = useMemo(() => {
    if (!selection) {
      return [];
    }

    return allVenues.filter(
      venue =>
        venue.sharedMenuLink.scope === selection.scope.type && venue.sharedMenuLink.sharedMenuId === selection.id,
    );
  }, [selection, allVenues]);

  const {
    shownVenues,
    selectedVenues,
    selectedVenueIds,
    onRowClick,
    searchInput,
    searchVenues,
    selectAllShownVenues,
    selectedShownVenues,
    deselectAllShownVenues,
    uniqueCities,
    setAppliedFilters,
  } = useVenueList({
    venues: venuesLinkedToSharedMenu,
  });

  const venueFilterMenuItems = buildVenueFilterMenuItems(uniqueCities);

  const {
    handleClearAll: clearAllFilters,
    handleGroupClick: toggleFilterGroup,
    handleItemClick: selectFilter,
    expandedGroups: expandedFilterGroups,
    expandAllGroups: expandAllFilterGroups,
    selectedIds: selectedFilterIds,
  } = useSelectableAccordion(venueFilterMenuItems, setAppliedFilters);

  const areAllVenuesSelected = allVenues.length === selectedVenueIds.size;

  if (!selection) {
    return null;
  }

  const isSelectionDraft = getSelectionState(selection) === 'draft';

  return (
    <SidepanelRoot
      ContentComponent={SidepanelContentComponent}
      disableContentPadding
      onDismiss={onClose}
      open
      sizePreset="sideEndSheet:extraLarge"
    >
      <SidepanelHeader closeButtonAriaLabel="Close" onCloseClick={onClose}>
        <FormattedMessage
          id="listing-manager.selections.venue-management.title"
          values={{ menuName: localize(selection.name, selection.primary_language) }}
        />
      </SidepanelHeader>
      <SidepanelContent disablePadding>
        <SidepanelToolbar>
          <SidepanelToolbarLeft>
            <SidepanelSearchInput
              value={searchInput}
              icon={<MagnifyingGlass />}
              placeholder={formatMessage({ id: 'shared-menu.toolbar.search-placeholder' })}
              onChange={e => searchVenues(e.target.value)}
            />
            {venuesLinkedToSharedMenu.length > 0 && (
              <FilterMenu appliedFiltersCount={selectedFilterIds.length}>
                <SelectableAccordion
                  headerLabel={formatMessage({ id: 'shared-menu.toolbar.filter-venues.title' })}
                  items={venueFilterMenuItems}
                  expandedGroups={expandedFilterGroups}
                  onItemClick={selectFilter}
                  onGroupClick={toggleFilterGroup}
                  onClearAllClick={clearAllFilters}
                  expandAllGroups={expandAllFilterGroups}
                  selectedIds={selectedFilterIds}
                  expandedByDefault
                />
              </FilterMenu>
            )}
          </SidepanelToolbarLeft>
          <SidepanelToolbarRight>
            {selectedVenueIds.size > 0 && (
              <StopSharingButton onClick={() => setIsStopSharingModalOpen(true)}>
                <FormattedMessage id="shared-menu.unlink.button-label" />
              </StopSharingButton>
            )}

            {!isSelectionDraft && (
              <AddVenuesButton onClick={() => setIsShareMenuModalOpen(true)}>
                <FormattedMessage id="listing-manager.selections.venue-management.share.add-venues" />
              </AddVenuesButton>
            )}
          </SidepanelToolbarRight>
        </SidepanelToolbar>
        {selectedVenues.length > 0 && <SelectedVenuesV2 venues={selectedVenues} onDeselectVenue={onRowClick} />}
        <SidepanelVenueList>
          {(!shownVenues || shownVenues.length === 0) && (
            <SidepanelEmptyMessage title={formatMessage({ id: 'menu.menu-sync.empty-venue-list' })} />
          )}
          {shownVenues && shownVenues.length > 0 && (
            <AutoSizer disableWidth>
              {({ height }) => (
                <FixedSizeList<VenueRowData>
                  height={height}
                  width="100%"
                  itemData={{
                    onRowClick,
                    selectedVenueIds,
                    venues: shownVenues,
                  }}
                  itemCount={shownVenues.length}
                  itemSize={ROW_HEIGHT}
                  itemKey={index => shownVenues[index].id}
                  overscanCount={6}
                >
                  {VenueListRowRenderer}
                </FixedSizeList>
              )}
            </AutoSizer>
          )}
        </SidepanelVenueList>
      </SidepanelContent>
      <ModalFooter>
        {shownVenues && (
          <VenueListFooter
            onDeselectAll={deselectAllShownVenues}
            onSelectAll={selectAllShownVenues}
            shownVenues={shownVenues}
            selectedShownVenues={selectedShownVenues}
          />
        )}
      </ModalFooter>

      {isShareMenuModalOpen && (
        <ShareWithVenuesModal
          isOpen
          onClose={() => setIsShareMenuModalOpen(false)}
          venues={allVenues}
          sharedMenu={selection}
        />
      )}

      {isStopSharingModalOpen && (
        <StopSharingWithVenuesModal
          isOpen
          onClose={() => setIsStopSharingModalOpen(false)}
          selectedVenueIds={[...selectedVenueIds]}
          sharedMenuId={selection.id}
          allVenuesSelected={areAllVenuesSelected}
          search={searchInput}
        />
      )}
    </SidepanelRoot>
  );
};
